import { Injectable, OnDestroy } from '@angular/core';
import { AuthService as Auth0Service, User } from '@auth0/auth0-angular';
import { Store } from '@ngrx/store';
import * as Sentry from '@sentry/browser';
import { Subject } from 'rxjs';
import { filter, take, takeUntil, tap } from 'rxjs/operators';
import { OrganizationAccesses } from 'src/app/admin/models/organization.model';
import { AnalyticsService } from 'src/app/shared/services/analytics.service';
import { PosthogService } from 'src/app/shared/services/posthog.service';
import { AppStore } from '../store.model';
import {
  SetOrganizationAccesses,
  LoadOrganizationConfig,
  SetUserApiError,
  SetUserId,
  SetUserName,
  SetUserOrganization,
  SetUserRoles,
} from './user.actions';
import { UserApiError, UserApiErrorTypes } from './user.errors';
import { OrganizationSubscriptionEffectActions } from '../organization-subscription/organization-subscription.actions';
import { OrganizationSubscriptionSelectors } from '../organization-subscription/organization-subscription.selectors';
import { OrganizationTypes } from 'src/app/shared/enums/organization.enum';
import { GoogleTagManagerService } from 'angular-google-tag-manager';

@Injectable()
export class AuthLegacyService implements OnDestroy {
  protected destroyed$ = new Subject();

  constructor(
    private store: Store<AppStore>,
    private analytics: AnalyticsService,
    private posthog: PosthogService,
    public auth0: Auth0Service,
    private gtmService: GoogleTagManagerService,
  ) {}

  public ngOnDestroy() {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  public observeEvents() {
    // Explicitly init GTM
    this.gtmService.addGtmToDom();

    this.auth0.user$.pipe(takeUntil(this.destroyed$)).subscribe((user) => this.handleUser(user));
    this.auth0.error$
      .pipe(takeUntil(this.destroyed$))
      .subscribe((error) => this.handleError(error));
  }

  public refreshUserToken() {
    return this.auth0.getAccessTokenSilently({ cacheMode: 'off' });
  }

  public logout() {
    this.analytics.track({
      context: 'auth',
      object: 'user',
      action: 'loggedOut',
    });

    // Tell analytics to forget the user
    this.analytics.unsetUserProperties();
    this.posthog.unsetUserProperties();

    // Clear Sentry user
    Sentry.setUser(null);

    this.auth0.logout({ logoutParams: { returnTo: window.location.origin } });
  }

  private async handleUser(user: User) {
    // Gets called on logout
    if (!user) {
      return;
    }

    const id = user.sub;
    const roles: string[] = user['https://soulmachines.com/roles'] || [];
    const orgId: number = user['https://soulmachines.com/organizationId'];
    const orgName: string = user['https://soulmachines.com/organizationName'];
    const orgDisplayName: string = user['https://soulmachines.com/organizationDisplayName'];
    const orgType: string = user['https://soulmachines.com/organizationType'];
    const orgRoles: string[] = user['https://soulmachines.com/organizationRoles'] || [];
    const orgAccesses: OrganizationAccesses = user['organizationAccesses'] || {};

    // Tell sentry who the user is
    Sentry.setUser({ id });

    // Tell analytics who the user is
    this.analytics.setUserProperties(id, {
      roles,
      orgId,
      orgType,
    });
    this.posthog.setUserProperties(id, {
      roles,
      orgId,
      orgType,
    });

    // Add user to store
    this.store.dispatch(OrganizationSubscriptionEffectActions.loadSubscription());
    this.store.dispatch(SetUserName({ familyName: user.family_name, givenName: user.given_name }));
    this.store.dispatch(SetUserRoles({ roles }));
    this.store.dispatch(
      SetUserOrganization({
        id: orgId,
        name: orgName,
        displayName: orgDisplayName,
        orgType,
        orgRoles,
      }),
    );
    this.store.dispatch(SetOrganizationAccesses({ organizationAccesses: orgAccesses }));
    this.store.dispatch(SetUserId({ id }));
    this.store.dispatch(LoadOrganizationConfig());

    // Track login event
    this.setGtmLoginEvent(orgType as OrganizationTypes, user);
  }

  private setGtmLoginEvent(organizationType: OrganizationTypes, user: User) {
    this.store
      .select(OrganizationSubscriptionSelectors.selectSubscription)
      .pipe(
        filter((s) => !!s),
        take(1),
        tap((subscription) => {
          const subscriptionItem = subscription?.items?.[0];
          if (subscriptionItem) {
            const gtmTag = {
              event: 'Studio user login successful',
              organizationType,
              interval: subscriptionItem.interval,
              userEmail: user.email,
              userId: user.sub,
            };

            this.gtmService.pushTag(gtmTag);
          }
        }),
      )
      .subscribe();
  }

  private handleError(error: Error) {
    this.store.dispatch(
      SetUserApiError({ error: new UserApiError(UserApiErrorTypes.AuthenticationError, error) }),
    );
  }
}
